import { z } from "zod";

import { fullAddressSchema } from "../lib/address/types";
import { DayOfTheWeek } from "../lib/dates/types";

export const facilityPhotoSchema = z.object({
  _id: z.string(),
  index: z.string(),
  name: z.string(),
  fileStorageFileKey: z.string().optional(),
  fileStorageRootFolder: z.string().optional(),
  url: z.string().optional(),
});

export type FacilityPhoto = z.infer<typeof facilityPhotoSchema>;

export const facilityRatingsResponseSchema = z.object({
  total: z.number(),
  avg: z.number(),
});

export type FacilityRatings = z.infer<typeof facilityRatingsResponseSchema>;

export const facilityProfileResponseSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  name: z.string(),
  type: z.string(),
  requiresLunchBreak: z.boolean().nullish(),
});

export type FacilityProfile = z.infer<typeof facilityProfileResponseSchema>;

export enum VerificationContactMethod {
  PHONE = "PHONE",
  EMAIL = "EMAIL",
  TEXT = "TEXT",
}
export enum FacilityVerificationMethod {
  TIMESHEET = "Timesheet Only",
  OUTBOUND = "Outbound Verification",
  SHEET = "External Google Sheet",
  OTHER = "Other",
}

const verificationContactSchema = z.object({
  archived: z.boolean(),
  contactMethod: z.object({
    name: z.nativeEnum(VerificationContactMethod).optional(),
  }),
  availability: z
    .array(
      z.object({
        day: z.nativeEnum(DayOfTheWeek),
      })
    )
    .optional(),
});

export const facilityVerificationPreferencesSchema = z.object({
  name: z.string().optional(),
  maxOutreachAttemptsPerDay: z.number().optional(),
  usesTimesheets: z.boolean().optional(),
  verificationMethod: z
    .object({
      name: z.nativeEnum(FacilityVerificationMethod),
    })
    .optional(),
  contacts: z.array(verificationContactSchema).optional(),
  outboundContactList: z.array(verificationContactSchema).optional(),
  preferenceType: z.string().optional(),
  verificationAttempt: z.number().optional(),
  requiresLunchBreak: z.boolean().optional(),
  mongodbId: z.string().optional(),
});
export type FacilityVerificationPreferences = z.infer<typeof facilityVerificationPreferencesSchema>;

export const facilityVerificationPreferencesResponseSchema = z.object({
  data: z.array(facilityVerificationPreferencesSchema),
  success: z.boolean(),
});
export type FacilityVerificationPreferencesResponse = z.infer<
  typeof facilityVerificationPreferencesResponseSchema
>;
export const authorizedSignatorySchema = z.object({
  _id: z.string(),
  name: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  role: z.string().nullish(),
});

export enum FacilityTypes {
  BEHAVIOURAL_HEALTH_CLINIC = "Behavioural Health Clinic",
  CRISIS_CENTER = "Crisis Center",
  DENTAL_CLINIC = "Dental Clinic",
  EXPLORATORY_REMOTE = "Exploratory Remote",
  HOME_HEALTHCARE = "Home Healthcare",
  HOSPICE = "Hospice",
  HOSPITAL = "Hospital",
  HOTEL = "Hotel",
  LONG_TERM_ACUTE_CENTER = "Long Term Acute Center",
  LONG_TERM_CARE = "Long Term Care",
  MEDICAL_LAB = "Medical Lab",
  NURSING_HOME = "Nursing Home",
  PHARMACY = "Pharmacy",
  PSYCH_HOSPITAL = "Psych Hospital",
  SUBSTANCE_ABUSE_DETOX_CENTER = "Substance Abuse Detox Center",
  SURGERY_CENTER = "Surgery Center",
  LONG_TERM_ACUTE_CARE_HOSPITAL = "Long-term Acute Care Hospital",
}

export enum FacilityNoteCategoryIdentifier {
  PROFILE_DESCRIPTION = "PROFILE_DESC",
  MANDATORY_BREAK_POLICY = "MANDATORY_BREAK_POLICY",
}

export const baseFacilityNoteSchema = z.object({
  category: z.string(),
  categoryId: z.string(),
  description: z.string(),
  hideInput: z.boolean(),
  sortOrder: z.number(),
  note: z.string().nullish(),
  noteId: z.string().nullish(),
  identifier: z.string().nullish(),
  updatedAt: z.string().nullish(),
  updatedBy: z.string().nullish(),
  isVisibleToWorker: z.boolean(),
  categoryTextVisibleToWorker: z.string(),
});

type BaseFacilityNote = z.infer<typeof baseFacilityNoteSchema>;

export const mandatoryBreakPolicyNote = baseFacilityNoteSchema.extend({
  note: z.string(),
  noteId: z.string(),
  identifier: z.literal("MANDATORY_BREAK_POLICY"),
  updatedAt: z.string(),
  updatedBy: z.string(),
});

export type MandatoryBreakPolicyNote = z.infer<typeof mandatoryBreakPolicyNote>;

export function isMandatoryBreakPolicyNote(value: any): value is MandatoryBreakPolicyNote {
  return value?.identifier === "MANDATORY_BREAK_POLICY";
}

export const facilityNoteSchema = z.union([mandatoryBreakPolicyNote, baseFacilityNoteSchema]);

export type FacilityNote = BaseFacilityNote;

export const facilityNoteAcknowledgementSchema = z.object({
  category: z.string(),
  identifier: z.union([z.literal("MANDATORY_BREAK_POLICY"), z.string()]),
  note: z.string(),
  updatedAt: z.string(),
  noteId: z.string(),
});

export type FacilityNoteAcknowledgement = z.infer<typeof facilityNoteAcknowledgementSchema>;

export const facilitySchema = z.object({
  name: z.string(),
  userId: z.string().optional(),
  geoLocation: z.object({
    coordinates: z.tuple([z.number(), z.number()]),
    type: z.string(),
  }),
  verificationPreference: facilityVerificationPreferencesSchema.optional(),
  state: z.string().optional(),
  tmz: z.string().optional(),
  /**
   * @deprecated You are probably looking for `userId` and not this.
   * This is just a MongoDB's auto-generated internal ID which we don't regard as the facility ID in our system
   */
  _id: z.string().optional(),
  email: z.string().optional(),
  facilityId: z.string().optional(),
  phone: z.string().optional(),
  requiredDocuments: z.array(z.string()).optional(),
  fullAddress: fullAddressSchema.optional(),
  authorizedSignatories: z.array(authorizedSignatorySchema).optional(),
  requireTimecardPhoto: z.boolean().optional(),
  rates: z.record(z.string(), z.number()).optional(),
  shiftConfirmationInstructions: z.string().optional(),
  covidData: z
    .object({
      dateOfFirstCovidActivePatient: z.string().nullish(),
      covidActive: z.boolean().nullish(),
      lastManualUpdate: z.string().nullish(),
      confirmedResidentLastMonth: z.number().nullish(),
      confirmedResidentLastWeek: z.number().nullish(),
    })
    .optional(),
  type: z.nativeEnum(FacilityTypes).optional(),
  lateCancellation: z
    .object({
      period: z.number(),
      feeHours: z.number(),
    })
    .optional(),
  sentHomeChargeHours: z.number().optional(),
  distance: z.number().optional(),
  isApproxDistance: z.boolean().optional().optional(),
  checkInInstructions: z.string().optional(),
});

export type Facility = z.infer<typeof facilitySchema>;

export const shiftSlotSchema = z.object({
  workplaceUserId: z.string(),
  stats: z.record(z.string(), z.object({ assignedShifts: z.number(), totalShifts: z.number() })),
  start: z.string(),
  end: z.string(),
  bookedBy: z.array(
    z.object({
      name: z.string(),
      userId: z.string(),
      qualification: z.string(),
      hasProfilePic: z.boolean(),
    })
  ),
});

export type ShiftSlot = z.infer<typeof shiftSlotSchema>;
